import React, { useEffect, useMemo, useRef, useState } from 'react';
import { carteraApi } from './api';
import { money, iniciales, avaColor, EtapaChip, vehiculoTxt } from './helpers';
import { NuevoContratoModal } from './NuevoContratoModal';

const ESTATUS_SEG = [
  ['todas', 'Todas'], ['vigente', 'Al corriente'], ['vencido', 'Vencidos'],
  ['deteriorado', 'Deteriorados'], ['liquidado', 'Liquidados'], ['castigado', 'Castigados'], ['recuperado', 'Recuperados'],
];
const TIPO_SEG = [['todos', 'Todo producto'], ['AP', 'Puro'], ['AF', 'Financiero'], ['CS', 'Crédito simple']];

const PROD_TAG = {
  arrendamiento_puro: { bg: '#E4F5FB', color: '#1f6f8c' },
  arrendamiento_financiero: { bg: '#E5F8EF', color: '#06794b' },
  credito_simple: { bg: '#F5F3FF', color: '#6D28D9' },
};

const PER_PAGE = 20;

export const CarteraLista = ({ onOpen }) => {
  const [estatus, setEstatus] = useState('todas');
  const [tipo, setTipo] = useState('todos');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNuevo, setShowNuevo] = useState(false); // modal de alta manual
  const [reload, setReload] = useState(0);            // fuerza refetch tras crear
  const abortRef = useRef(null);

  const params = useMemo(() => {
    const p = { page, per_page: PER_PAGE };
    if (estatus !== 'todas') p.estatus = estatus;
    if (tipo !== 'todos') p.tipo = tipo;
    if (search.trim()) p.search = search.trim();
    return p;
  }, [estatus, tipo, search, page]);

  useEffect(() => {
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const t = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await carteraApi.getCartera(params, ctrl.signal);
        if (ctrl.signal.aborted) return;
        setData(res?.data?.data || []);
        setPagination(res?.data?.pagination || null);
      } catch (e) {
        if (ctrl.signal.aborted || e?.code === 'ERR_CANCELED') return;
        setError(e?.response?.data?.message || 'No se pudo cargar la cartera.');
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, search ? 300 : 0); // pequeño debounce al escribir
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [params, search, reload]);

  const setFiltro = (setter) => (v) => { setPage(1); setter(v); };

  const totalCartera = data.reduce((a, c) => a + Number(c.saldo_insoluto || 0), 0);
  const totalMora = data.reduce((a, c) => a + Number(c.mora_acumulada || 0), 0);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Operación</div>
          <h2>Cartera de clientes</h2>
          <p>
            {pagination?.total ?? data.length} contratos · {money(totalCartera)} en página · {money(totalMora)} de mora
          </p>
        </div>
        <div className="spacer" />
        <button className="btn btn-ghost btn-sm"><i className="pi pi-upload" />Subir cartera (Excel)</button>
        <button className="btn btn-green btn-sm" onClick={() => setShowNuevo(true)}><i className="pi pi-plus" />Nuevo contrato</button>
      </div>

      <div className="filters">
        <div className="seg">
          {ESTATUS_SEG.map(([v, l]) => (
            <button key={v} className={estatus === v ? 'on' : ''} onClick={() => setFiltro(setEstatus)(v)}>{l}</button>
          ))}
        </div>
        <div className="spacer" style={{ flex: 1 }} />
        <div className="seg green">
          {TIPO_SEG.map(([v, l]) => (
            <button key={v} className={tipo === v ? 'on' : ''} onClick={() => setFiltro(setTipo)(v)}>{l}</button>
          ))}
        </div>
        <div className="search-box">
          <i className="pi pi-search" />
          <input
            placeholder="Buscar cliente o clave…"
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
          />
        </div>
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        {loading && data.length === 0 ? (
          <div className="cart-loading">Cargando cartera…</div>
        ) : error ? (
          <div className="cart-loading">{error}</div>
        ) : data.length === 0 ? (
          <div className="empty-state" style={{ padding: 40 }}>
            <i className="pi pi-search" />
            <div className="es-title">Sin resultados</div>
            <div className="es-sub">Ajusta los filtros o la búsqueda.</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="tbl" style={{ minWidth: 980 }}>
              <thead>
                <tr>
                  <th style={{ paddingLeft: 22 }}>Cliente</th>
                  <th>Vehículo</th>
                  <th>Producto</th>
                  <th>Avance</th>
                  <th className="right">Tasa</th>
                  <th>Estatus</th>
                  <th className="right">Saldo</th>
                  <th className="right" style={{ paddingRight: 22 }}>Mora</th>
                </tr>
              </thead>
              <tbody>
                {data.map((c) => {
                  const total = c.cuotas?.total || 0;
                  const pagadas = c.cuotas?.pagadas || 0;
                  const pct = total ? Math.round((pagadas / total) * 100) : 0;
                  const prod = PROD_TAG[c.tipo] || { bg: '#F1F5F8', color: '#475569' };
                  return (
                    <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => onOpen?.(c.id)}>
                      <td style={{ paddingLeft: 22 }}>
                        <div className="cust-cell">
                          <div className="ava-sm" style={{ background: avaColor(c.cliente || c.clave_contrato) }}>{iniciales(c.cliente)}</div>
                          <div>
                            <div className="nm">{c.cliente || '—'}</div>
                            <div className="sub">{c.clave_contrato}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="t-sm fw6">{vehiculoTxt(c.vehiculo)}</div>
                        <div className="t-xs muted">{c.plazo_meses} meses</div>
                      </td>
                      <td><span className="tag-prod" style={{ background: prod.bg, color: prod.color }}>{c.tipo_label || c.tipo}</span></td>
                      <td style={{ minWidth: 120 }}>
                        <div className="between" style={{ marginBottom: 4 }}>
                          <span className="t-xs fw6">{pagadas}/{total}</span>
                          <span className="t-xs muted">{pct}%</span>
                        </div>
                        <div className="mini-bar"><span style={{ width: pct + '%' }} /></div>
                      </td>
                      <td className="right t-sm fw6">{c.tasa != null ? c.tasa + '%' : '—'}</td>
                      <td><EtapaChip etapa={c.etapa_cobranza} estatus={c.estatus} enRevision={c.en_revision} recuperado={c.cierre?.motivo === 'recuperacion_auto'} sub={c.dias_vencido ? c.dias_vencido + 'd' : null} /></td>
                      <td className="right"><span className="money">{money(c.saldo_insoluto)}</span></td>
                      <td className="right" style={{ paddingRight: 22 }}>
                        {Number(c.mora_acumulada) > 0
                          ? <span className="money neg">{money(c.mora_acumulada)}</span>
                          : <span className="muted t-sm">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pagination && pagination.last_page > 1 && (
        <div className="cart-pager">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</button>
          <span>Página {pagination.current_page} de {pagination.last_page} · {pagination.total} contratos</span>
          <button disabled={page >= pagination.last_page} onClick={() => setPage((p) => p + 1)}>Siguiente</button>
        </div>
      )}

      {showNuevo && (
        <NuevoContratoModal
          onClose={() => setShowNuevo(false)}
          onSaved={() => setReload((n) => n + 1)}
          onOpen={(id) => { setShowNuevo(false); onOpen?.(id); }}
        />
      )}
    </>
  );
};

export default CarteraLista;
